import hcluster from "@/assets/hcluster";
import lodash from "@/assets/lodash";
import CONSTANTS from "@/constants";

/**
 * Use Vue's plugin mechanism to make the Dataservice available to every plugin as a global property. This removes the
 * Dataservice (and with it the dataset) from the list of reactive properties and saves us a huge amount of watchers.

 * @type {{install(*, *): void}}
 */
export const DataServicePlugin = {
  install(Vue, opts) {
    Vue.prototype.$dataService = new DataService(opts.Dataset);
  }
};

export default class DataService {
  /**
   * The DataService provides an interface for all the necessary transformations of the letious datasets. This way the
   * components cannot and should not interact directly with the data.
   *
   * @param dataset Object that adheres to the project's scheme for datasets.
   */
  constructor(dataset = this.isRequired()) {
    if (!dataset.nodes) {
      throw new Error("Dataset is not in correct format: nodes missing.");
    }

    if (!dataset.versions) {
      throw new Error("Dataset is not in correct format: versions missing.");
    }

    if (dataset.versions[0].links.timestamp) {
      throw new Error(
        "Dataset has not been converted. It still uses 'timestamp' instead of 'timestep'."
      );
    }

    this.dataset = dataset;
    // Disable similarity-based row ordering
    // this.sortedIndex = this.getSortedIndex();
    this.sortedIndex = lodash.range(dataset.versions.length);

    /**
     * Lookup table: get Node index by id.
     * @type {{}}
     */
    this.nodeToIndex = {};
    dataset.nodes.forEach((n, i) => {
      this.nodeToIndex[n.id] = i;
    });

    /**
     * Lookup table: get Node id by index.
     * @type {Array}
     */
    this.indexToNode = dataset.nodes.map(n => n.id);

    this.nodeIdToName = {};
    this.dataset.nodes.forEach(
      node => (this.nodeIdToName[node.id] = node.description)
    );

    // TODO
    this.$data = this.buildLinksArray(dataset);

    this.dataIds = {
      nodes: this.dataset.nodes.map(node => node.id),
      timesteps: Object.keys(this.$data[0]).map((key, index) => index),
      variants: Object.keys(this.$data).map((key, index) => index)
    };

    /**
     * The highest node value (=aggregated degree), across the whole dataset. Useful for nomalization of cell heights.
     * @type {{grouped: Number, single: Number}}
     */
    this.maxNodeValue = this.getHighestDegree();
  }

  /**
   * Transforms the dataset to an array of objects for easier and faster access:
   *  `const links = buildLinksArray(dataset);`
   *  `const degreeIn = links[variant][timestep][nodeId].in`
   *  `const degreeOut = links[variant][timestep][nodeId].out`
   *  `const degreeAggregated = links[variant][timestep][nodeId].agg`
   *
   * @param dataset
   * @returns {Object}
   */
  buildLinksArray(dataset) {
    let result = {};

    let maxTimestep = 0;
    dataset.versions.forEach(v => {
      maxTimestep = Math.max(
        maxTimestep,
        lodash.maxBy(v.links, l => l.timestep).timestep
      );
    });

    dataset.versions.forEach((v, variant) => {
      // First dimension of the arrays is the variant index
      result[variant] = {};

      for (const timestep of lodash.range(maxTimestep + 1)) {
        result[variant][timestep] = {};
        dataset.nodes.forEach(node => {
          result[variant][timestep][node.id] = {
            in: 0, // Number of incoming edges
            out: 0, // Number of outgoing edges
            agg: 0, // Number of aggregated edges (in+out)
            linkSources: [], // Array of ids for incoming edges (an entry here means there is an edge from linkSources.id to node.id)
            linkTargets: [] // opposite of linkSources
          };
        });
        v.links
          .filter(l => l.timestep === timestep)
          .forEach(l => {
            // Update metrics for the links's source
            result[variant][timestep][l.source].out += 1;
            result[variant][timestep][l.source].agg += 1;
            result[variant][timestep][l.source].linkTargets.push(l.target);

            // Update metrics for the links's target
            result[variant][timestep][l.target].in += 1;
            result[variant][timestep][l.target].agg += 1;
            result[variant][timestep][l.target].linkSources.push(l.source);
          });
      }
    });
    return result;
  }

  /**
   * Calculates an object to draw a node-link diagram from. Properties:
   *  - node.value = sum of in- and outgoing edges, without self-edges
   *  - links = Set of unique edges, across all variants, excluding self-edges
   *
   * @param selection {Object} Has to contain either `nodes`, `variants`, or `timesteps`.
   * @returns {{nodes: Array, links: Array}}
   */
  toNodeLinkObject(selection) {
    const nodes = this.dataset.nodes.map(n => ({
      id: n.id,
      description: n.description,
      value: 1
    }));

    const variants =
      selection && selection.variants.size > 0
        ? Array.from(selection.variants)
        : Object.keys(this.$data);

    const timesteps =
      selection && selection.timesteps.size > 0
        ? Array.from(selection.timesteps)
        : Object.keys(this.$data[0]);

    let linkMat = [...Array(nodes.length).fill([])];
    linkMat.forEach(
      (v, idx) => (linkMat[idx] = [...Array(nodes.length).fill(0)])
    );

    variants.forEach(variant => {
      timesteps.forEach(timestep => {
        nodes.forEach(node => {
          if (
            selection &&
            selection.nodes.size > 0 &&
            !selection.nodes.has(node.id)
          ) {
            return;
          }

          const nodeData = this.$data[variant][timestep][node.id];

          nodes.find(n => n.id === node.id).value += nodeData.agg;

          nodeData.linkTargets.forEach(target => {
            const sourceIdx = this.nodeToIndex[node.id];
            const targetIdx = this.nodeToIndex[target];
            linkMat[sourceIdx][targetIdx] += 1;
          });

          nodeData.linkSources.forEach(source => {
            const sourceIdx = this.nodeToIndex[source];
            const targetIdx = this.nodeToIndex[node.id];
            linkMat[sourceIdx][targetIdx] += 1;
          });
        });
      });
    });

    let links = [];
    linkMat.forEach((sourceRow, source) => {
      sourceRow.forEach((value, target) => {
        if (value > 0) {
          links.push({
            source: this.indexToNode[source],
            target: this.indexToNode[target],
            value: value
          });
        }
      });
    });

    return { nodes: nodes, links: links };
  }

  /**
   * @param width Width of the displaying element in px.
   * @param height Height of the displaying element in px.
   * @param selection {Object} (optional) if given, returns a filtered grid.
   * @returns {{colLabels: Array, rows: Array, rowLabels: Array}}
   */
  to2DGrid(width = this.isRequired(), height = this.isRequired(), selection) {
    /**
     * The possibly selected node, else `undefined`.
     * @type {Number}
     */
    let nodes = new Set();
    if (selection !== undefined && Object.keys(selection).includes("nodes")) {
      nodes = selection.nodes;
    }

    /**
     * The possibly selected columns, else `undefined`.
     * @type {Set}
     */
    let columns = new Set();
    if (selection !== undefined && Object.keys(selection).includes("columns")) {
      columns = selection.columns;
    }

    /**
     * The possibly selected rows, else `undefined`.
     * @type {Set}
     */
    let rows = new Set();
    if (selection !== undefined && Object.keys(selection).includes("rows")) {
      rows = selection.rows;
    }

    /**
     * Dirty hack to ensure that upon initialization the selection is not undefined.
     * @type {{nodes: Number, columns: Set, rows: Set}}
     */
    selection = {
      rows: rows,
      columns: columns,
      nodes: nodes
    };

    /**
     * A dense 2D array of edge counts across all timesteps and variant. Filtered by a possibly selected node.
     * @type {Array}
     */
    const data = this.filterGridByNodes(nodes);

    /**
     * Constant factor to what selected cells' width/height are scaled by.
     * @type {number}
     */
    const SCALE_FACTOR = 4;

    let xpos = 0;
    let ypos = 0;

    /**
     * In case a row/col is selected add SCALE_FACTOR amount of rows/cols.
     * In fact: add SCALE_FACTOR - 1 rows/cols, as the original row/col is already added.
     * @type {number}
     */
    const COLS_TO_ADD = columns.size * (SCALE_FACTOR - 1);

    const rowLabelWidth = 100;
    const n_cols = data[0].length + COLS_TO_ADD;
    /**
     * Used for not selected cells. Based on the number of overall cells and the available svg width.
     * @type {number}
     */
    let baseCellWidth = (width - rowLabelWidth) / n_cols;

    /**
     * See `COLS_TO_ADD`.
     * @type {number}
     */
    const ROWS_TO_ADD = rows.size * (SCALE_FACTOR - 1);

    const n_rows = data.length + ROWS_TO_ADD;
    const colLabelHeight = 30;
    /**
     * Used for not selected cells. Based on the number of overall cells and the available svg height.
     * @type {number}
     */
    let baseRowHeight = (height - colLabelHeight) / n_rows;

    /**
     * To enable independent display of rows, row labels, and colum labels the have to be self-contained.
     * So every information needed for display (x/y-coords, width, height, etc.) have to be included.
     * @type {{colLabels: Array, rows: Array, rowLabels: Array}}
     */
    let result = {
      rows: [],
      rowLabels: [],
      colLabels: []
    };

    this.sortedIndex.forEach(i => {
      const row = data[i];
      let result_row = [];

      // In case the current row is selected, multiply the height by SCALE_FACTOR
      let rowHeight = baseRowHeight;
      rowHeight = rows.has(i) ? baseRowHeight * SCALE_FACTOR : baseRowHeight;

      row.forEach((col, j) => {
        // In case the current column is selected, multiply the width by SCALE_FACTOR
        let cellWidth = baseCellWidth;

        if (columns.has(j)) {
          cellWidth = baseCellWidth * SCALE_FACTOR;
        }

        // Dont update the `colLabels` array on every cell. Only do this for the first row!
        if (i === 0) {
          result.colLabels.push({
            id: j,
            text: `t${j}`,
            width: cellWidth,
            height: colLabelHeight,
            x: xpos,
            y: 0
          });
        }

        let type = this.getCellType(i, j, selection);
        let cellData = this.getCellData(
          { row_id: i, column_id: j },
          selection,
          type,
          { width: cellWidth, height: rowHeight }
        );

        result_row.push({
          x: xpos,
          y: ypos,
          width: cellWidth,
          height: rowHeight,
          row_id: i,
          column_id: j,
          cell_id: `cell-${i}-${j}`,
          type: type,
          cellData: cellData
        });
        xpos += cellWidth;
      });

      result.rows.push({
        row_id: i,
        row_x: 0,
        row_y: ypos + rowHeight,
        row_height: rowHeight,
        cells: result_row
      });

      result.rowLabels.push({
        id: i,
        x: 0,
        y: ypos + rowHeight,
        height: rowHeight,
        width: rowLabelWidth
      });

      xpos = 0;
      ypos += rowHeight;
    });
    return result;
  }

  /**
   *
   * @param nodes {Set}
   * @returns {this}
   */
  filterGridByNodes(nodes) {
    let result = [...Array(Object.keys(this.$data).length)].fill(0);

    let maxTimestep = 0;
    for (const variant in this.$data) {
      maxTimestep = Math.max(
        maxTimestep,
        Object.keys(this.$data[variant]).length
      );
    }

    for (const variant in this.$data) {
      result[variant] = Array(maxTimestep).fill(0);

      for (const timestep of lodash.range(maxTimestep)) {
        result[variant][timestep] = Array(maxTimestep).fill(0);
        if (nodes !== undefined && nodes.size > 0) {
          let agg = [];
          nodes.forEach(n => {
            agg.push(this.$data[variant][timestep][n]);
          });
          result[variant][timestep] = agg
            .map(a => a.agg)
            .reduce(function(pv, cv) {
              return pv + cv;
            }, 0);
        } else {
          let agg = 0;
          for (const node in this.$data[variant][timestep]) {
            agg += this.$data[variant][timestep][node].agg;
          }
          result[variant][timestep] = agg;
        }
      }
    }
    return result;
  }

  isRequired = () => {
    throw new Error("param is required");
  };

  /**
   * @returns {number[]} Array of integers, containing the (sorted) order of row indices.
   */
  getSortedIndex() {
    const dataDictionary = this.dataset;
    let adjacencyMatrices = this.makeAdjacencyMatricesForIndividualTimesteps();
    let dataForCluster = [];
    for (let i in dataDictionary["versions"]) {
      dataForCluster.push({
        name: " " + dataDictionary["versions"][i]["description"],
        position: i
      });
    }
    let colorCluster = hcluster()
      .distance((a, b) =>
        DataService.similarityVariant(adjacencyMatrices, a, b)
      )
      .linkage("avg")
      .data(dataForCluster);
    return colorCluster.tree().indexes;
  }

  /**
   *
   * @param row_id
   * @param column_id
   * @param selection
   * @returns {string}
   */
  getCellType(row_id, column_id, selection) {
    let type = CONSTANTS.CELL_TYPE.SINGLE_BAR;
    if (selection === undefined) {
      return type;
    }
    if (selection.rows.has(row_id)) {
      type = CONSTANTS.CELL_TYPE.HORIZONTAL_BARS;
    }

    if (selection.columns.has(column_id)) {
      type = CONSTANTS.CELL_TYPE.VERTICAL_BARS;
    }

    if (selection.rows.has(row_id) && selection.columns.has(column_id)) {
      type = CONSTANTS.CELL_TYPE.MATRIX;
    }
    return type;
  }

  /**
   *
   * @param idx {{row_id: number, column_id: number}} identifies the currently drawn cell.
   * @param selection {{rows: Set, columns: Set, node_id: (number|undefined)}} identifies the currently selected row/col/node.
   * @param type {CONSTANTS.CELL_TYPE} what chart to prepare the data for.
   * @param dimensions {{height: number, width: number}} contains width and height.
   * @returns {Array}
   */
  getCellData(idx, selection, type, dimensions) {
    switch (type) {
      case CONSTANTS.CELL_TYPE.MATRIX:
        return this.getAdjacencyMatrix(idx);
      case CONSTANTS.CELL_TYPE.HORIZONTAL_BARS:
      case CONSTANTS.CELL_TYPE.VERTICAL_BARS:
        return this.getBarsGroupedByNode(idx, selection);
      default:
        return this.getSingleBar(idx, selection, dimensions);
    }
  }

  /**
   * Returns the sum of all aggregates degrees for the given timestep/variant. Can be filtered to a node.
   * @param idx
   * @param selection
   * @param dimensions
   * @returns {Array}
   */
  getSingleBar(idx, selection, dimensions) {
    // Use selected or ALL nodes
    const nodes =
      selection.nodes.size === 0
        ? this.dataset.nodes.map(n => n.id)
        : Array.from(selection.nodes);

    let result = [];
    const value = nodes
      .map(node => this.$data[idx.row_id][idx.column_id][node].agg)
      .reduce((a, b) => a + b, 0); // == sum
    const barHeight = dimensions.height * (value / this.maxNodeValue.single);
    result.push({
      value: value,
      bar_height: barHeight,
      filler_height: dimensions.height - barHeight
    });
    return result;
  }

  /**
   * Get aggregated degree per node for a given timestep/variant.
   * @param idx
   * @param selection
   * @returns {Array}
   */
  getBarsGroupedByNode(idx, selection) {
    const row = idx.row_id;
    const col = idx.column_id;

    if (selection.row_id === row && selection.column_id === col) return [];

    let result = [];

    // Use selected or ALL nodes
    const nodes =
      selection.nodes.size === 0
        ? this.dataset.nodes.map(n => n.id)
        : Array.from(selection.nodes);

    // In case one or more nodes are selected, return their aggregated degree.
    this.dataset.nodes
      .map(n => n.id)
      .forEach(node => {
        let value = 0;
        if (nodes.includes(node)) {
          value = this.$data[row][col][node].agg;
        } else {
          // For all not-selected nodes, we want to have the number of edges to selected nodes
          const nodeData = this.$data[row][col][node];
          const allLinks = nodeData.linkSources.concat(nodeData.linkTargets);
          nodes.forEach(n => (value += allLinks.filter(l => l === n).length));
        }
        result.push({
          id: node,
          value: value
        });
      });

    return result;
  }

  /**
   */
  getHighestDegree() {
    let result = { grouped: 0, single: 0 };

    Object.keys(this.$data).forEach(variant => {
      Object.keys(this.$data[variant]).forEach(timestep => {
        Object.keys(this.$data[variant][timestep]).forEach(node_id => {
          const node = this.$data[variant][timestep][node_id];
          result.grouped = Math.max(result.grouped, node.agg);
        });
        const value = this.dataset.nodes
          .map(n => n.id)
          .map(node => this.$data[variant][timestep][node].agg)
          .reduce((a, b) => a + b, 0);
        result.single = Math.max(result.single, value);
      });
    });

    return result;
  }

  /**
   * @param idx {{row_id, column_id}}
   */
  getAdjacencyMatrix(idx) {
    let matrix = [...Array(Object.keys(this.dataset.nodes).length)].fill(0);
    for (const node in this.$data[idx.row_id][idx.column_id]) {
      const nodeIdx = this.nodeToIndex[node];
      matrix[nodeIdx] = [...Array(matrix.length)].fill(0);
      this.$data[idx.row_id][idx.column_id][node].linkTargets.forEach(
        target => {
          matrix[nodeIdx][this.nodeToIndex[target]] += 1;
        }
      );
    }
    return matrix;
  }

  makeAdjacencyMatricesForIndividualTimesteps() {
    let matrixArrayForVariants = [];
    let nodesArray = this.dataset.nodes;
    let nodesDictionary = {};
    for (let i = 0; i < nodesArray.length; i++) {
      if (!(nodesArray[i]["id"] in nodesDictionary)) {
        nodesDictionary[nodesArray[i]["id"]] = i;
      }
    }
    let matrixDictionaryPerTimestep = {};
    let edgesArray = [];

    for (let i = 0; i < this.dataset.versions.length; i++) {
      matrixDictionaryPerTimestep = {};
      edgesArray = this.dataset.versions[i]["links"];
      for (let j = 0; j < edgesArray.length; j++) {
        if (!(edgesArray[j]["timestep"] in matrixDictionaryPerTimestep)) {
          matrixDictionaryPerTimestep[edgesArray[j]["timestep"]] = new Array(
            this.dataset.nodes.length
          );
          let temp = matrixDictionaryPerTimestep[edgesArray[j]["timestep"]];
          for (let k = 0; k < temp.length; k++) {
            temp[k] = new Array(this.dataset.nodes.length).fill(0);
          }
        }

        matrixDictionaryPerTimestep[edgesArray[j]["timestep"]][
          nodesDictionary[edgesArray[j]["source"]]
        ][nodesDictionary[edgesArray[j]["target"]]]++;
      }
      matrixArrayForVariants.push(matrixDictionaryPerTimestep);
    }
    return matrixArrayForVariants;
  }

  mapIdsToNames(type, ids) {
    switch (type) {
      case "nodes":
        return ids.map(id => this.nodeIdToName[id]);
      case "timesteps":
        return ids.map(id => "t" + id);
      case "variants":
        return ids.map(id => "v" + id);
    }
    return ids;
  }

  static jaccardSimilarity(matrix1, matrix2) {
    let countOfCommonEdges = 0;
    let countOfAllEdges = 0;
    for (let i = 0; i < matrix1.length; i++) {
      let range = matrix1[i].length;
      for (let j = 0; j < range; j++) {
        if (matrix1[i][j] > 0 && matrix2[i][j] > 0) {
          countOfCommonEdges++;
        }
        if (matrix1[i][j] > 0 || matrix2[i][j] > 0) {
          countOfAllEdges++;
        }
      }
    }
    return countOfCommonEdges / countOfAllEdges;
  }

  static similarityVariant(adjacencyMatrices, variant1Index, variant2Index) {
    let similarity = 0;

    let matrix1 = adjacencyMatrices[variant1Index];
    let matrix2 = adjacencyMatrices[variant2Index];
    let keysDict = {};
    for (let key in matrix1) {
      if (!(key in keysDict)) keysDict[key] = true;
    }
    for (let key in matrix2) {
      if (!(key in keysDict)) keysDict[key] = true;
    }
    let allTimesteps = Object.keys(keysDict);
    for (let i in allTimesteps) {
      allTimesteps[i] = +allTimesteps[i];
    }
    allTimesteps.sort((a, b) => a - b);

    for (let i in allTimesteps) {
      let tempkey = allTimesteps[i];
      if (tempkey in matrix1 && tempkey in matrix2) {
        similarity += DataService.jaccardSimilarity(
          matrix1[tempkey],
          matrix2[tempkey]
        );
      }
    }
    return similarity;
  }
}
